/**
 * =============================================================================
 * BrokenAssetsAdminWatcher.tsx
 * -----------------------------------------------------------------------------
 * Watcher globale attivo SOLO per gli utenti con ruolo amministratore o
 * responsabile. Intercetta gli eventi `error` delle <img> a livello document
 * (capture phase) e:
 *   - logga in console un warning strutturato con src + URL della pagina;
 *   - applica un piccolo badge rosso "asset rotto" sovrapposto all'immagine,
 *     visibile solo all'admin, per individuarla a colpo d'occhio nel sito
 *     reale (non in editor).
 *
 * Motivazione: anche con il trigger DB e i prebuild check, una pagina
 * pubblica pu\u00f2 finire con un asset rotto per cause "esterne" al contenuto
 * editoriale (es. file rimosso da public/ per refactor, CDN bucket
 * cambiato). Questo watcher \u00e8 il terzo livello di difesa: la presenza
 * del badge segnala all'admin un problema da risolvere senza richiedere
 * azione esplicita.
 *
 * Il componente non renderizza nulla nel DOM; lavora con effetti collaterali.
 * =============================================================================
 */

import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

const BADGE_DATA_ATTR = "data-broken-asset-badged";

export function BrokenAssetsAdminWatcher() {
  const { hasRole } = useAuth();
  const isAdmin = !!(hasRole?.("amministratore") || hasRole?.("responsabile"));

  useEffect(() => {
    if (!isAdmin || typeof window === "undefined") return;

    const onError = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.tagName !== "IMG") return;
      const img = target as HTMLImageElement;

      // Evita doppi badge se l'errore si ripete (immagini con retry).
      if (img.dataset.brokenAssetBadged === "1") return;
      img.dataset.brokenAssetBadged = "1";

      // Log strutturato in console per debugging / collezione futura.
      // Non usiamo console.error per non far esplodere i tool di monitoring.
      console.warn(
        "[admin][broken-asset] Immagine non caricata:",
        { src: img.src, page: window.location.href, alt: img.alt },
      );

      // Badge visuale: container con position:relative + label assoluto.
      // Wrapping leggero per non rompere layout esistenti: aggiungiamo
      // un overlay come sibling se l'<img> ha un genitore stile-compatibile,
      // altrimenti annotiamo solo l'attributo data-* e usiamo un outline
      // come fallback per renderla visibile.
      try {
        img.style.outline = "2px dashed hsl(0 84% 60%)";
        img.style.outlineOffset = "2px";
        img.title = (img.title ? img.title + " \u00b7 " : "") +
          "[Admin] Asset rotto: " + img.src;

        const parent = img.parentElement;
        if (parent) {
          // Position relative se non lo \u00e8 gi\u00e0, per ancorare il badge.
          const computed = getComputedStyle(parent);
          if (computed.position === "static") {
            parent.style.position = "relative";
          }
          const badge = document.createElement("span");
          badge.textContent = "Asset rotto · copia";
          badge.setAttribute(BADGE_DATA_ATTR, "1");
          badge.title =
            "Clicca per copiare i dettagli dell'asset rotto (src + pagina) negli appunti.";
          badge.style.cssText = [
            "position:absolute",
            "top:4px",
            "left:4px",
            "z-index:50",
            "background:hsl(0 84% 60%)",
            "color:white",
            "font-size:10px",
            "font-weight:600",
            "padding:2px 6px",
            "border-radius:4px",
            "font-family:ui-sans-serif,system-ui,sans-serif",
            "pointer-events:auto",
            "cursor:pointer",
            "user-select:text",
            "box-shadow:0 1px 2px rgba(0,0,0,.2)",
          ].join(";");
          // Click sul badge: copia un report testuale pronto da incollare
          // (in chat, prompt AI, ticket di assistenza, ecc.).
          badge.addEventListener("click", async (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            const payload = [
              "[Asset rotto]",
              `src: ${img.src}`,
              `alt: ${img.alt || "(nessuno)"}`,
              `pagina: ${window.location.href}`,
              `quando: ${new Date().toISOString()}`,
            ].join("\n");
            try {
              await navigator.clipboard.writeText(payload);
              badge.textContent = "Copiato ✓";
              setTimeout(() => {
                badge.textContent = "Asset rotto · copia";
              }, 1500);
            } catch {
              badge.textContent = "Copia fallita";
            }
          });
          parent.appendChild(badge);
        }
      } catch {
        // best-effort: in caso di iframe / shadow DOM rinunciamo al badge
        // visuale ma il log in console resta.
      }
    };

    // capture=true \u00e8 obbligatorio: gli eventi `error` su <img> NON fanno bubbling.
    document.addEventListener("error", onError, true);
    return () => {
      document.removeEventListener("error", onError, true);
      // Pulizia badge esistenti (cambio ruolo / unmount).
      document
        .querySelectorAll(`span[${BADGE_DATA_ATTR}]`)
        .forEach((el) => el.remove());
    };
  }, [isAdmin]);

  return null;
}